import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Calendar, Clock, MapPin } from 'lucide-react'
import { eventsApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import Card from '../../components/ui/Card'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import MediaImage from '../../components/ui/MediaImage'
import { fadeInUp, staggerContainer } from '../../utils/animations'
import { formatDate } from '../../utils/date'

export default function EventsList() {
  const { t } = useTranslation()
  const { localized } = useLanguage()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    eventsApi
      .getAll()
      .then(({ data }) => setEvents(data.data || data || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="header-offset">
      <div className="gradient-brand py-12 text-white sm:py-16">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold sm:text-4xl"
          >
            {t('events.title')}
          </motion.h1>
          <p className="mt-4 text-white/80">{t('events.subtitle')}</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        {loading ? (
          <LoadingSpinner className="py-20" />
        ) : events.length === 0 ? (
          <p className="py-20 text-center text-text/60">{t('events.empty')}</p>
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-6"
          >
            {events.map((event) => (
              <motion.div key={event.id} variants={fadeInUp}>
                <Card padding={false} className="overflow-hidden">
                  <div className="flex flex-col md:flex-row">
                    <Link to={`/events/${event.slug}`} className="flex flex-1 flex-col md:flex-row">
                      <div className="relative h-48 w-full overflow-hidden md:h-auto md:w-72">
                        <MediaImage
                          src={event.image || event.featured_image}
                          alt={localized(event, 'title')}
                          fallbackIndex={event.id}
                          className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                        />
                      </div>
                      <div className="flex flex-1 flex-col justify-center p-6">
                        <h2 className="mb-3 text-xl font-bold text-primary hover:text-secondary">
                          {localized(event, 'title')}
                        </h2>
                        <div className="flex flex-wrap gap-4 text-sm text-text/60">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="h-4 w-4 text-accent" />
                            {(event.start_date || event.event_date) &&
                              formatDate(event.start_date || event.event_date)}
                          </span>
                          {event.time && (
                            <span className="flex items-center gap-1.5">
                              <Clock className="h-4 w-4 text-accent" />
                              {event.time}
                            </span>
                          )}
                          <span className="flex items-center gap-1.5">
                            <MapPin className="h-4 w-4 text-accent" />
                            {localized(event, 'location')}
                          </span>
                        </div>
                      </div>
                    </Link>
                    <div className="flex items-center border-t border-border p-4 md:border-s md:border-t-0 md:px-6">
                      <Link
                        to={`/contact?subject=inquiry&event=${encodeURIComponent(event.slug)}`}
                        className="w-full rounded-full bg-accent px-5 py-3 text-center text-sm font-semibold text-white hover:bg-primary md:w-auto md:py-2.5"
                      >
                        {t('events.register')}
                      </Link>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  )
}
